import { Icon, type IconName } from "./Icon";

/** "PDF", "PNG", "ZIP": a file's extension, for a tile when there's no preview. */
export function fileExtension(filename: string): string {
  const dot = filename.lastIndexOf(".");
  return dot > 0 ? filename.slice(dot + 1, dot + 5) : "file";
}

export type FileKind = "html" | "markdown" | "pdf" | "image" | "archive" | "other";

const KINDS: Record<string, FileKind> = {
  html: "html",
  htm: "html",
  md: "markdown",
  markdown: "markdown",
  pdf: "pdf",
  png: "image",
  jpg: "image",
  jpeg: "image",
  gif: "image",
  webp: "image",
  svg: "image",
  zip: "archive",
  gz: "archive",
  tgz: "archive",
  "7z": "archive",
  rar: "archive",
};
const ICONS: Record<FileKind, IconName> = {
  html: "code",
  markdown: "doc",
  pdf: "pdf",
  image: "image",
  archive: "archive",
  other: "file",
};

/** What sort of file it is, by extension: picks its icon, and whether a browser can open it. */
export function fileKind(filename: string): FileKind {
  const dot = filename.lastIndexOf(".");
  return (dot > 0 && KINDS[filename.slice(dot + 1).toLowerCase()]) || "other";
}

/** A small icon for a file's type (HTML, Markdown, PDF, image, archive, anything else). */
export function FileIcon({ filename }: { filename: string }) {
  const kind = fileKind(filename);
  return (
    <span className="file-icon" data-kind={kind} aria-hidden="true">
      <Icon name={ICONS[kind]} />
    </span>
  );
}

/** A file's thumbnail: its image preview when it has one (`src`), else a tile with its
    extension. With `onOpen`, the preview is a button that opens the full image. */
export function FileThumb({
  filename,
  src,
  onOpen,
}: {
  filename: string;
  src?: string | null;
  onOpen?: () => void;
}) {
  if (!src)
    return (
      <span className="file-thumb" aria-hidden="true">
        {fileExtension(filename)}
      </span>
    );
  const img = <img src={src} alt="" loading="lazy" />;
  return onOpen ? (
    <button type="button" className="file-thumb" aria-label={`View ${filename}`} onClick={onOpen}>
      {img}
    </button>
  ) : (
    <span className="file-thumb">{img}</span>
  );
}
